import { Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { medicalsApi } from "../../../../../../../api/medicalsApi";
import useNotifications from "../../../../../../../hooks/useNotifications";
import { useUnsavedWhileEditing } from "../../../../../../../hooks/useUnsavedWhileEditing";
import {
  TCopilot,
  TCopilotNotePopulationStatus,
} from "../../../../../../../types/copilot";
import { useCopilotClinicalContext } from "../../useCopilotClinicalContext";
import CopilotSectionAcceptButton from "./CopilotSectionAcceptButton";
import CopilotSectionEditedBadge from "./CopilotSectionEditedBadge";
import {
  CopilotHumanEditedSection,
  resolveHumanEditedSectionFromNoteField,
  withSectionHumanEditedExtension,
} from "../utils/copilotExtensions";

interface EditableSectionProps {
  title?: string;
  content: any;
  renderContent: (content: any, readOnly: boolean) => React.ReactNode;
  onEdit?: (newContent: any) => void;
  editable?: boolean;
  field?: string;
  editOnTitle?: boolean;
  onPopulate?: () => Promise<unknown>;
  /** Override apply/clear sections when field mapping is insufficient. */
  applySections?: CopilotHumanEditedSection[];
}

function resolveApplySections(
  field?: string,
  applySections?: CopilotHumanEditedSection[],
): CopilotHumanEditedSection[] {
  if (applySections?.length) {
    return applySections;
  }
  const section = field ? resolveHumanEditedSectionFromNoteField(field) : null;
  return section ? [section] : [];
}

function resolvePopulationStatusField(
  field?: string,
): keyof TCopilotNotePopulationStatus | undefined {
  if (field === "chiefComplaint" || field === "hpi") {
    return field;
  }
  return undefined;
}

function hasContentToAccept(content: unknown): boolean {
  if (content == null || content === "") {
    return false;
  }
  if (typeof content === "string") {
    return content.trim().length > 0;
  }
  return true;
}

const EditableSection = ({
  title,
  content,
  renderContent,
  onEdit,
  editable = false,
  field,
  editOnTitle = true,
  onPopulate,
  applySections,
}: EditableSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [isSaving, setIsSaving] = useState(false);
  const { copilotData, setCopilotData, encounterHeaders, hasLinkedEncounter } =
    useCopilotClinicalContext();
  const { setNotification } = useNotifications();
  useUnsavedWhileEditing(
    `copilot-note-${field || title || "section"}`,
    isEditing,
  );

  const humanEditedSection = field
    ? resolveHumanEditedSectionFromNoteField(field)
    : null;
  const sectionsToApply = resolveApplySections(field, applySections);
  const populationStatusField = resolvePopulationStatusField(field);

  const handleEditClick = () => {
    if (!copilotData) return;
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!field || !copilotData?.id) return;

    try {
      setIsSaving(true);

      const notePayload: TCopilot = {
        ...copilotData,
        note: {
          ...copilotData.note,
          [field]: editedContent,
        },
      };
      const updatedNote: TCopilot = humanEditedSection
        ? withSectionHumanEditedExtension(notePayload, humanEditedSection)
        : notePayload;

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedNote,
        encounterHeaders,
      );

      setCopilotData(response.data);
      setNotification({
        message: `${title} updated successfully`,
        variant: "success",
      });

      if (onEdit) {
        onEdit(editedContent);
      }
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setNotification({
        message: `Failed to update ${title?.toLowerCase()}`,
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {title && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">{title}</Typography>
            {humanEditedSection && (
              <CopilotSectionEditedBadge section={humanEditedSection} />
            )}
            {editable && editOnTitle && !isEditing && (
              <IconButton size="small" onClick={handleEditClick}>
                <Edit fontSize="small" />
              </IconButton>
            )}
          </Box>
          {onPopulate && hasLinkedEncounter && hasContentToAccept(content) && (
            <CopilotSectionAcceptButton
              sections={sectionsToApply}
              populationStatusField={populationStatusField}
              onAccept={onPopulate}
            />
          )}
        </Box>
      )}

      {isEditing ? (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
          />
          <Box
            sx={{ mt: 1, display: "flex", gap: 1, justifyContent: "flex-end" }}
          >
            <Button
              size="small"
              onClick={() => setIsEditing(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={handleSave}
              loading={isSaving}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <>
          {content ? (
            renderContent(content, !editable)
          ) : (
            <Typography variant="body2" color="GrayText">
              No {title?.toLowerCase()} recorded
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

export default EditableSection;
